'use client';

import { showToast } from '@/shared/utils/toast';
import { createElement } from 'react';

// Notification category types matching backend
type NotificationCategory =
  | 'all_client_messages'
  | 'assigned_client_messages'
  | 'team_chat'
  | 'assignments'
  | 'system_errors'
  | 'security_alerts'
  | 'marketing'
  | 'billing';

interface NotificationData {
  id?: string;
  title: string;
  body?: string;
  category?: NotificationCategory;
  metadata?: {
    conversationId?: string;
    messageId?: string;
    contactId?: string;
    senderId?: string;
    senderName?: string;
    contactName?: string;
  };
}

// SVG Icons as React elements
const icons = {
  // Envelope icon for SMS/inbox messages
  message: createElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
    createElement('rect', { width: 20, height: 16, x: 2, y: 4, rx: 2 }),
    createElement('path', { d: 'm22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7' })
  ),

  // Chat bubble for team messages
  teamChat: createElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
    createElement('path', { d: 'M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2z' }),
    createElement('path', { d: 'M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1' })
  ),

  // User plus for assignments
  assignment: createElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
    createElement('path', { d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' }),
    createElement('circle', { cx: 9, cy: 7, r: 4 }),
    createElement('line', { x1: 19, x2: 19, y1: 8, y2: 14 }),
    createElement('line', { x1: 22, x2: 16, y1: 11, y2: 11 })
  ),

  // Bell for general notifications
  bell: createElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
    createElement('path', { d: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9' }),
    createElement('path', { d: 'M10.3 21a1.94 1.94 0 0 0 3.4 0' })
  ),

  // Credit card for billing
  billing: createElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
    createElement('rect', { width: 20, height: 14, x: 2, y: 5, rx: 2 }),
    createElement('line', { x1: 2, x2: 22, y1: 10, y2: 10 })
  ),

  // Shield for security
  security: createElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
    createElement('path', { d: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z' })
  ),
};

/**
 * Get target URL for notification navigation
 */
function getTargetUrl(notification: NotificationData): string {
  const category = notification.category || 'all_client_messages';
  const conversationId = notification.metadata?.conversationId;

  // Team chat - use URL param so teams page can read it
  if (category === 'team_chat' && conversationId) {
    return `/teams?conversation=${conversationId}`;
  }

  // Inbox messages
  if ((category === 'all_client_messages' || category === 'assigned_client_messages') && conversationId) {
    return `/dashboard?conversation=${conversationId}`;
  }

  // Assignments
  if (category === 'assignments' && notification.metadata?.contactId) {
    return `/contacts/${notification.metadata.contactId}`;
  }

  // Billing
  if (category === 'billing') {
    return '/settings?tab=billing';
  }

  // Default
  return '/notifications';
}

/**
 * Format notification based on category
 */
function formatNotification(notification: NotificationData): {
  title: string;
  description: string;
  icon: React.ReactElement;
} {
  const category = notification.category || 'all_client_messages';

  switch (category) {
    // SMS/Inbox messages - "New message received" + "From [Name/Number]"
    case 'all_client_messages':
    case 'assigned_client_messages':
      return {
        title: 'New message received',
        description: `From ${notification.title}`,
        icon: icons.message,
      };

    // Team chat - "New team message" + "From [Sender]"
    case 'team_chat':
      return {
        title: 'New team message',
        description: `From ${notification.title}`,
        icon: icons.teamChat,
      };

    // Assignments - "New assignment" + "[Contact] assigned to you"
    case 'assignments':
      return {
        title: 'New assignment',
        description: notification.body || `${notification.title} assigned to you`,
        icon: icons.assignment,
      };

    // Billing notifications
    case 'billing':
      return {
        title: notification.title || 'Billing update',
        description: notification.body || 'View your billing details',
        icon: icons.billing,
      };

    // Security alerts
    case 'security_alerts':
      return {
        title: notification.title || 'Security alert',
        description: notification.body || 'Review security details',
        icon: icons.security,
      };

    // System errors and others
    case 'system_errors':
    case 'marketing':
    default:
      return {
        title: notification.title,
        description: notification.body || '',
        icon: icons.bell,
      };
  }
}

/**
 * Show a notification toast using Sonner's built-in beautiful design
 */
export function showNotificationToast(notification: NotificationData) {
  const targetUrl = getTargetUrl(notification);
  const formatted = formatNotification(notification);

  // Use notification ID as toast ID to prevent duplicate toasts for the same notification
  const toastId = notification.id || `notification_${Date.now()}`;

  showToast.info(formatted.title, formatted.description, {
    id: toastId,
    icon: formatted.icon,
    duration: 5000,
    action: {
      label: 'View',
      onClick: () => {
        if (typeof window !== 'undefined') {
          window.location.href = targetUrl;
        }
      },
    },
  });
}

